// Mock data for Ankit Attri's Portfolio

/** Hero & positioning — swap in live CI/API data where noted */
export const heroCopy = {
  badge: "Reliability-focused software & product engineer",
  headline: "Building Reliable, Scalable Software Systems",
  subline:
    "I build and test end-to-end so systems scale, perform, and don’t break when reality hits.",
};

export const personalInfo = {
  name: "Ankit Attri",
  title: "Software Engineer · Quality & Reliability · Product-minded",
  tagline: heroCopy.headline,
  email: "ankitattri.pvt@gmail.com",
  phone: "+91 7016264021",
  location: "Bengaluru, India",
  profiles: {
    linkedin: "https://www.linkedin.com/in/ankitattri09/",
    github: "https://github.com/ankitattri09",
    leetcode: "https://leetcode.com/u/ankitattri09/",
    hackerrank: "https://www.hackerrank.com/profile/ankitattri_pvt",
    gfg: "https://www.geeksforgeeks.org/profile/ankitattri"
  },
  stats: [
    { label: "Years Experience", value: "2+" },
    { label: "Projects Delivered", value: "10+" },
    // { label: "HackerRank Stars", value: "5★" }
  ]
};

/** Illustrative snapshot — replace with live CI/API hooks for production */
export const engineeringInsights = [
  { id: "ci", label: "Last CI run", value: "Passed", status: "ok", detail: "main @ a3f9c2d" },
  { id: "cov", label: "Test coverage", value: "87%", status: "ok", detail: "API + service layers" },
  { id: "api", label: "APIs tested", value: "100+", status: "ok", detail: "contract & negative paths" },
  { id: "fail", label: "Failures caught", value: "3", status: "warn", detail: "this week, pre-merge" },
];

export const impactStrip = [
  "Concurrent systems & thread pools",
  "~25% UDP reliability lift (internship)",
  "100+ APIs in automated layers",
  "Payment / wallet risks surfaced early",
  "Mobile flows in CI (login → campaign → pay)",
];

export const systemHealth = {
  build: "portfolio-web #184",
  deployedAt: "2h ago",
  environment: "staging",
  status: "healthy",
  automationFlows: ["Login", "Campaign", "Payments"],
};

export const testTrendWeek = [
  { day: "Mon", passed: 412, failed: 6 },
  { day: "Tue", passed: 428, failed: 4 },
  { day: "Wed", passed: 401, failed: 9 },
  { day: "Thu", passed: 439, failed: 3 },
  { day: "Fri", passed: 445, failed: 2 },
  { day: "Sat", passed: 118, failed: 1 },
  { day: "Sun", passed: 96, failed: 0 },
];

export const whatIDo = [
  {
    title: "Engineering",
    subtitle: "Build",
    bullets: [
      "Backend & APIs",
      "Concurrent / async design",
      "System-level performance",
      "Ship in prod-like constraints",
    ],
  },
  {
    title: "Quality",
    subtitle: "Break & harden",
    bullets: [
      "Appium, API automation, CI gates",
      "Root-cause and flake hunting",
      "Failure modes under load",
      "Logs you can act on",
    ],
  },
  {
    title: "Product",
    subtitle: "Improve",
    bullets: [
      "Journeys & edge cases early",
      "Impact over output",
      "Tighter critical flows",
      "Bridge eng ↔ stakeholders",
    ],
  },
];

export const contactCopy = {
  title: "Let’s build something reliable",
  subtitle: "Open to software engineering, reliability/QA, and product-minded technical roles.",
};

export const demoScenarios = {
  login: {
    title: "Login flow (API)",
    log: [
      { t: 0, line: "[RUN] POST /v1/auth/login — valid credentials" },
      { t: 450, line: "[PASS] 200 OK — token issued, refresh cookie set" },
      { t: 900, line: "[RUN] GET /v1/me — authorized" },
      { t: 1250, line: "[PASS] 200 OK — profile matches fixture" },
      { t: 1650, line: "[DONE] Login journey — 2 assertions, 840ms total" },
    ],
  },
  payment: {
    title: "Payment failure & retry",
    log: [
      { t: 0, line: "[RUN] POST /v1/payments/debit — simulate gateway 503" },
      { t: 500, line: "[FAIL] 503 Service Unavailable — idempotency-key preserved" },
      { t: 950, line: "[INFO] Retry policy: exponential backoff, max 3" },
      { t: 1400, line: "[RUN] POST /v1/payments/debit — retry #2" },
      { t: 1850, line: "[PASS] 200 OK — wallet debited, ledger consistent" },
      { t: 2300, line: "[DONE] Failure path — retry + reconciliation verified" },
    ],
  },
};

export const aiToolkit = [
  {
    title: "AI test ideas",
    description: "Paste an OpenAPI snippet → outline cases (happy, 4xx, idempotency, race).",
    example: 'Input: POST /payments → Output: "duplicate idempotency key", "insufficient balance", timeout…',
  },
  {
    title: "Bug triage assistant",
    description: "Paste a stack trace or CI log → suggested buckets: env, race, regression, infra.",
    example: "Timeout after 30s → check connection pool + downstream latency before blaming the test.",
  },
];

export const about = {
  summary:
    "I build features, break them on purpose, and connect both to how users actually move through the product. Flutter & APIs by day, Appium/Jest pipelines in CI, C++ systems background—breadth without losing depth.",
  highlights: [
    "End-to-end: ship code + own quality for the same surfaces",
    "APIs & mobile automation (Jest, Supertest, Appium, TestNG)",
    "Systems thinking on payments, auth, and flaky reality",
    "Prefer measurable reliability over buzzwords",
  ],
};

export const skills = [
  {
    category: "Development",
    items: [
      { name: "React.js", level: 90 },
      { name: "Next.js", level: 85 },
      { name: "Node.js", level: 85 },
      { name: "MongoDB", level: 80 },
      { name: "Flutter", level: 80 },
      { name: "JavaScript/TypeScript", level: 90 }
    ]
  },
  {
    category: "Testing & Quality",
    items: [
      { name: "Selenium", level: 95 },
      { name: "Appium", level: 90 },
      { name: "Jest/Supertest", level: 85 },
      { name: "API Testing", level: 90 },
      { name: "Test Automation", level: 95 }
    ]
  },
  {
    category: "Languages & Tools",
    items: [
      { name: "Java", level: 90 },
      { name: "Python", level: 85 },
      { name: "C++", level: 80 },
      { name: "Git/GitHub", level: 90 },
      { name: "Docker", level: 75 }
    ]
  },
  {
    category: "Product & Leadership",
    items: [
      { name: "Product Thinking", level: 85 },
      { name: "Team Leadership", level: 85 },
      { name: "Agile/Scrum", level: 80 },
      { name: "Technical Mentoring", level: 85 },
      { name: "Cross-functional Collaboration", level: 90 }
    ]
  }
];

export const experience = [
  {
    id: 1,
    company: "WLDD Pvt Ltd",
    role: "Software Development Engineer in Test",
    location: "Bengaluru",
    duration: "December 2024 - Present",
    responsibilities: [
      "Contributed to end-to-end development and QA of mobile apps (Solo for Creators, Meme'd) using Flutter and Dart",
      "Built automated API test suites with Jest and Supertest, ensuring backend reliability and contract integrity",
      "Owned mobile test strategy using Appium automation in Java with TestNG for release stability",
      "Collaborated with product and design teams during feature implementation"
    ],
    techStack: ["Flutter", "Dart", "Appium", "Jest", "Supertest", "Java", "TestNG", "GitLab", "GitHub Actions"]
  },
  {
    id: 2,
    company: "LTIMindtree",
    role: "Software Development Engineer in Test",
    location: "Bengaluru",
    duration: "June 2024 - November 2024",
    responsibilities: [
      "Delivered automation solutions for Delta Air Lines and Virgin Atlantic Airlines",
      "Implemented Selenium automation using hybrid and BDD frameworks",
      "Ensured robust test coverage and system stability through comprehensive testing strategies",
      "Drove functional reliability through manual and edge-case testing"
    ],
    techStack: ["Selenium", "BDD", "Cucumber", "TestNG", "JUnit", "Java"]
  },
  {
    id: 3,
    company: "Jio Platforms Limited",
    role: "Software Development Intern",
    location: "Mumbai",
    duration: "August 2023 - September 2023",
    responsibilities: [
      "Engineered multi-threaded client-server communication system in C++ on Ubuntu Server",
      "Elevated data transfer reliability by 25% through UDP connection optimization",
      "Architected efficient thread pool for concurrent task execution (up to 10 simultaneous tasks)"
    ],
    techStack: ["C++", "Ubuntu Server", "Multi-threading", "UDP", "System Programming"]
  },
  {
    id: 4,
    company: "Reliance Industries Limited",
    role: "Database Management Trainee",
    location: "Jamnagar",
    duration: "December 2022 - January 2023",
    responsibilities: [
      "Optimized complex SQL queries, reducing execution time by 25% for multi-table joins",
      "Elevated overall database performance and responsiveness on large datasets"
    ],
    techStack: ["SQL", "Database Optimization", "Query Tuning"]
  }
];

export const projects = [
  {
    id: 1,
    title: "Solo for Creators",
    description:
      "SDET ownership from early builds through production: strategy, API contracts, device automation, and release checks on the creator-facing app.",
    role: "SDET · mobile & API (WLDD)",
    impact: [
      "Quality tracked the product from first integrations through store-ready releases.",
      "Appium on critical creator flows; Jest/Supertest on backend contracts.",
      "CI-backed runs so regressions surface before they reach users.",
      "Worked with product and eng on scope, risk, and what “done” meant per release.",
    ],
    techStack: ["Flutter", "Dart", "Appium", "Java", "TestNG", "Jest", "Postman", "GitLab", "GitHub Actions"],
    categories: ["Mobile", "API automation", "SDET"],
  },
  {
    id: 2,
    title: "Meme'd",
    description:
      "End-to-end quality for a social, content-heavy Flutter app: journeys, APIs, and stability as features shipped toward release.",
    role: "SDET · journeys & API layers (WLDD)",
    impact: [
      "Strategy and execution from early builds through shipping, alongside feature work.",
      "Automated core paths (auth, campaigns, submissions) with Appium and supporting API checks.",
      "Exploratory and edge-case work alongside suites to catch what scripts miss.",
      "Tighter signal before release through GitLab pipelines and consistent environments.",
    ],
    techStack: ["Flutter", "Dart", "Appium", "Java", "TestNG", "Jest", "Hoppscotch", "GitLab"],
    categories: ["Mobile", "Social", "SDET"],
  },
];

export const certifications = [
  {
    id: 1,
    title: "Cyber Security Analyst Professional",
    issuer: "IBM",
    date: "February 2024",
    icon: "shield-check"
  },
  {
    id: 2,
    title: "Google Cloud Computing Foundations",
    issuer: "Google",
    date: "December 2023",
    icon: "cloud"
  },
  {
    id: 3,
    title: "Azure Administrator Associate",
    issuer: "Microsoft",
    date: "October 2023",
    icon: "server"
  }
];

export const achievements = [
  {
    id: 1,
    title: "5★ Problem Solving - HackerRank",
    description: "Achieved highest rating in problem-solving domain",
    icon: "star"
  },
  {
    id: 2,
    title: "IETE Senior Core Committee Member",
    description: "Led technical events and mentored students at VIT's IETE chapter (2022-2024)",
    icon: "users"
  },
  {
    id: 3,
    title: "Sports Captain",
    description: "Led student athletics and fostered team culture (2019-2021)",
    icon: "trophy"
  }
];

export const education = [
  {
    id: 1,
    institution: "Vellore Institute of Technology",
    degree: "Bachelor of Technology",
    field: "Computer Science Engineering",
    grade: "CGPA 8.7",
    duration: "July 2025"
  },
  {
    id: 2,
    institution: "Kokilaben Dhirubhai Ambani Reliance Foundation School",
    degree: "Class XII",
    field: "Higher Secondary Education",
    grade: "94.2%",
    duration: "June 2021"
  },
  {
    id: 3,
    institution: "Tagore Public School",
    degree: "Class X",
    field: "Secondary Education",
    grade: "95%",
    duration: "June 2019"
  }
];
